import { api, API_BASE } from './client'

// --- Questionnaire editor (admin authoring of custom questionnaires) ---------

export interface QEditorItem {
  id: number
  text: string
  subscale: string
  reverse: boolean
  kind: string
  required: boolean
  help_es: string | null
  options: { value: string; label: string }[]
  min_value: number | null
  max_value: number | null
  unit: string | null
  step: number | null
  min_label: string | null
  max_label: string | null
  max_length: number
}

export interface QEditorSubscale {
  key: string
  label: string
  items: number[]
  min_score: number
  max_score: number
  description: string
}

export interface QEditorDefinition {
  name_es: string
  name?: string
  instructions_es: string
  administration_es: string | null
  notes_es: string | null
  likert: { value: number; label: string }[]
  items: QEditorItem[]
  subscales: QEditorSubscale[]
  total_min: number | null
  total_max: number | null
  [k: string]: unknown
}

export interface QEditorVersion {
  id: number
  questionnaire_id: number
  version_no: number
  status: 'draft' | 'published' | 'archived'
  definition: QEditorDefinition
  created_at: string | null
  published_at: string | null
}

export interface QEditorValidatedSummary {
  kind: 'validated'
  code: string
  name_es: string
  n_items: number
  read_only: true
  editable: false
}

/**
 * Una entrada del VISOR de cuestionarios (`/admin/questionnaire-editor`).
 * Trae la definición COMPLETA — el visor muestra las preguntas, no sólo el
 * nombre — más la metadata de flujo: en qué olas se invita y dónde se
 * responde. Todo es de solo lectura por diseño (los cuestionarios los define
 * el protocolo, no el backoffice).
 */
export interface QCatalogEntry {
  code: string
  name_es: string
  name?: string
  kind: 'validado' | 'interno' | 'custom'
  source: string
  flow_es: string
  timepoints: string[]
  n_items: number
  response_count: number
  read_only: true
  definition: QEditorDefinition
  // Sólo en los `custom` (los que quedaron en base del editor viejo).
  id?: number
  archived?: boolean
  version_no?: number
  version_status?: string
  n_versions?: number
}

export interface QEditorCustomSummary {
  kind: 'custom'
  id: number
  code: string
  name_es: string
  read_only: false
  archived: boolean
  n_versions: number
  published_version_no: number | null
  has_draft: boolean
  draft_version_id: number | null
}

export interface ScoresSnapshot {
  subscales: Record<string, { raw: number; min: number; max: number; label: string }>
  total: { raw: number; min: number; max: number; label: string } | null
}

export type AthleteTimelineEvent =
  | {
      type: 'questionnaire'
      date: string | null
      response_id: number
      code: string
      timepoint: string
      scores: ScoresSnapshot
    }
  | {
      type: 'session'
      date: string | null
      session_id: string
      status: string
      n_trials: number
      goals: number
      saves: number
      misses: number
      score_sum: number
      goal_rate: number | null
    }

export interface AthleteTimeline {
  athlete: {
    user_id: number
    username: string
    email: string | null
    display_name: string | null
    club_id: number | null
  }
  events: AthleteTimelineEvent[]
}

export interface AdminActivityDay {
  day: string
  sessions: number
  closed: number
  aborted: number
  running: number
  pending: number
  other: number
}

export interface AdminStats {
  users_total: number
  sessions_total: number
  sessions_closed: number
  sessions_aborted: number
  trials_total: number
  dau_14d: number
  /** Backend sends `{ day: 'YYYY-MM-DD', sessions: number }` per row. */
  sessions_by_day_14d: Array<{ day: string; sessions: number }>
  session_activity_14d?: AdminActivityDay[]
  computed_at?: string
  period_start?: string
  period_end?: string
}

export interface AdminUserRow {
  id: number
  username: string
  email: string | null
  is_active: boolean
  is_superuser: boolean
  roles: string[]
  has_demo_access: boolean
  display_name: string | null
  n_sessions: number
  n_trials: number
  last_session_at: string | null
  date_joined: string | null
}

export interface AdminUserDetail {
  user: AdminUserRow
  profile: {
    display_name: string | null
    handedness: string | null
    dominant_foot: string | null
    height_cm: number | null
    club: { id: number; name: string } | null
  } | null
  activity: {
    n_sessions: number
    n_trials: number
    goal_rate: number | null
    outcomes: Record<string, number>
  }
  sessions: Array<{
    id: string
    started_at: string | null
    protocol_id: string | null
    condition: string | null
    status: string
  }>
  manageable_access_roles: string[]
}

/** Resumen de completitud que arma el servidor (`completeness_summary`,
 *  backend/app/services/completeness.py). Es evidencia propia del backend, no
 *  lo que declaró el casco al cerrar. */
export interface AdminSessionCompleteness {
  artifacts: {
    gameplay: boolean
    physio: boolean
    silver: boolean
    /** Lotes de gameplay aceptados DESPUÉS del sellado. */
    late_gameplay: number
  }
  /** Quién escribió el bronce fisiológico: `rpi_bio` | `browser_polar` | null. */
  physio_source: string | null
  /** Si esta sesión tenía que traer fisiología (abrió un Edge o ya llegó algo). */
  physio_expected: boolean
  /** Eje temporal Quest↔RPi (vrs-docs/36): sin él la fisiología del kit no se
   *  puede alinear con el gameplay, aunque el bronce esté entero. */
  clock_sync: SessionClockSync
  /** Motivos verificados por el servidor: `gameplay_gap`, `physio_bronze`, … */
  missing: string[]
  gameplay_events: number | null
  gameplay_gaps: number
  streams: Record<
    string,
    { chunks_received: number | null; chunk_seq_max: number | null; gaps: number }
  >
}

/** `completeness["clock_sync"]` — evidencia de silver, no del casco. */
export interface SessionClockSync {
  /** La fisiología que hay necesita un modelo de reloj (vino de la Pi). */
  expected: boolean
  /**
   * `not_needed` (browser o sin fisio) | `unknown` (silver no corrió todavía)
   * | `synced` | `unsynced` (silver no pudo ajustar el eje).
   */
  state: 'not_needed' | 'unknown' | 'synced' | 'unsynced'
  quality: string | null
  probes_accepted: number | null
  /** Lo que el casco dijo haber puesto en el cable al cerrar. */
  probes_sent_declared?: number | null
}

export interface AdminSessionRow extends AdminSessionCompleteness {
  id: string
  username?: string | null
  user_id: number
  status: string
  protocol_id: string | null
  condition: string | null
  started_at: string | null
  ended_at: string | null
  device_id: string | null
  sync_quality?: string | null
  /** Desde cuándo espera que un dispositivo traiga lo que falta. */
  pending_recovery_since: string | null
}

/** Un ciclo de `silver_regulation_cycles.jsonl.gz` (services/regulation_metrics.py). */
export interface SilverCycle {
  trial_id: string | null
  r_cardio: number | null
  r_cardio_eda: number | null
  quality: { cardio?: string | null; cardio_eda?: string | null }
  [k: string]: unknown
}

/** `GET /admin/sessions/{id}/silver` (admin_session_artifacts.py). Los
 *  manifests son opacos (JSON tal cual está en disco). */
export interface SessionSilver {
  session_id: string
  state: 'measured' | 'no_cycles' | 'processing' | 'no_signal'
  has_physio: boolean
  silver_path: string | null
  silver_materialized_at: string | null
  silver_clock_quality: string | null
  manifest: Record<string, unknown> | null
  regulation_manifest: Record<string, unknown> | null
  physio_manifest: Record<string, unknown> | null
  counts: {
    physio_samples: number | null
    cycles: number
    cardio_quality_counts: Record<string, number> | null
  }
  cycles: SilverCycle[]
}

export type ReportRoleView = 'athlete' | 'coach' | 'psychologist'

export interface AdminAuthEvent {
  id: number
  kind: string
  user_id: number | null
  identifier: string | null
  detail: string | null
  ip: string | null
  ts: string
}

export interface AdminQResponseRow {
  id: number
  code: string
  timepoint: string
  user_id: number
  username: string | null
  submitted_name: string | null
  submitted_email: string | null
  submitted_at: string
  scores: {
    subscales: Record<string, { raw: number; min: number; max: number; label: string }>
    total: { raw: number; min: number; max: number; label: string } | null
  }
}

export interface AdminQuestionnaireSummary {
  code: string
  name_es: string
  n_items: number
  response_count: number
}

export interface AdminInvitation {
  id: number
  email: string
  questionnaire_code: string
  timepoint: string | null
  invited_by_user_id: number | null
  created_at: string
  expires_at: string
  used_at: string | null
  email_sent_at: string | null
  revoked_at: string | null
  available_from: string | null
  status: 'pending' | 'scheduled' | 'used' | 'expired' | 'revoked'
  link_url: string
}

export interface AdminClub {
  id: number
  name: string
  logo_url: string | null
  primary_color: string | null
  secondary_color: string | null
  accent_color: string | null
  created_at: string
  seat_limit: number | null
  division: string | null
  /** Fecha (YYYY-MM-DD) hasta la que el servicio VR está habilitado. Null = sin vencimiento. */
  service_until: string | null
  /** Derivado: `service_until == null || service_until >= hoy`. */
  service_active: boolean
  player_count: number
  // --- Métricas operativas (app/services/club_stats.py). Siempre presentes:
  // un club recién creado trae ceros, no las omite.
  /** Filas `club_staff` del club (un solo rol: manager). */
  staff_count: number
  /** Cuántos de esos staff todavía no aceptaron los T&C del panel. */
  staff_pending_terms: number
  /** Invitaciones de staff vivas: link generado, sin usar y sin vencer. */
  staff_invites_pending: number
  kit_count: number
  kits_active: number
  /** Kits con una Raspberry vinculada ("kit unificado"). */
  kits_with_rpi: number
  /** Sesiones de jugadores del club en los últimos 7 días. */
  sessions_7d: number
  /** ISO de la última sesión de un jugador del club. Null = nunca entrenó. */
  last_session_at: string | null
}

export interface AdminStaffInvite {
  id: number
  club_id: number
  club_role: 'manager'
  email: string | null
  token: string
  status: 'pending' | 'used' | 'expired'
  created_at: string
  expires_at: string
  used_at: string | null
  used_by_user_id: number | null
  created_by_user_id: number | null
}

/**
 * Qué pasó con el MAIL al generar una invitación de staff. Lo único que el
 * backend sabe en ese momento es si el mail quedó encolado en el outbox
 * (`backend/app/services/outbox.py`): el despachador manda como mucho uno
 * cada `min_interval_s` y la entrega al buzón nunca se confirma. No existe
 * un "enviado" ni un "entregado" acá — la UI no debe inventarlo.
 */
export interface AdminStaffInviteEmail {
  /** Se encoló el mail (sólo si la invitación trae email). */
  queued: boolean
  /** Destinatario encolado, normalizado por el backend. Null si no se encoló. */
  recipient: string | null
  /** Fila de `email_outbox` — la misma que se ve en /admin/emails. */
  outbox_id: number | null
  /** Hay transporte de mail (SMTP o SendGrid). False = la cola no avanza. */
  transport_configured: boolean
  /** EMAIL_SENDING_ENABLED. False = se encola pero no se despacha. */
  sending_enabled: boolean
  /** Freno global: segundos mínimos entre dos mails. */
  min_interval_s: number
  /** ISO del próximo envío de la cola (no necesariamente este mail). */
  queue_next_send_at: string | null
  /** Mails en cola, incluido éste. */
  queue_pending: number
}

export interface AdminCreatedStaffInvite {
  token: string
  link_url: string
  expires_at: string
  invitation: AdminStaffInvite
  email: AdminStaffInviteEmail
}

export interface AdminClubDetail extends AdminClub {
  players: Array<{
    user_id: number
    display_name: string
    email: string | null
    date_joined: string | null
    last_login: string | null
  }>
}

export interface AdminConsent {
  id: number
  user_id: number
  consent_version: string
  signed_at: string
  is_parental: boolean
  recorded_by_user_id: number | null
  revoked_at: string | null
  notes: string | null
  created_at: string
}

export interface AdminEnrollment {
  id: number
  user_id: number
  study_name: string
  enrolled_at: string
  meets_inclusion: boolean
  exclusion_reason: string | null
  notes: string | null
  created_at: string
}

/**
 * A quién sumar como staff. El backend acepta exactamente uno de los dos:
 * `email` es lo que el operador conoce, `user_id` queda por compatibilidad
 * (POST /admin/clubs/{id}/staff, ver 15-backend-infra.md §7.5).
 */
export type AdminStaffTarget = { user_id: number } | { email: string }

/** A quién sumar al plantel: emails (lo que el operador tipea) y/o user_ids
 *  (cuando eligió de la búsqueda). Ver POST /admin/clubs/{id}/players. */
export type AdminPlayersTarget = { user_ids?: number[]; emails?: string[] }

export interface AdminAssignPlayersResult {
  club_id: number
  /** Cuántos cambiaron de club (los que ya estaban no cuentan). */
  updated: number
  /** Perfiles de jugador creados al vuelo para cuentas que no tenían. */
  created_profiles: number
  players: { user_id: number; email: string | null; display_name: string }[]
  /** Mails de alta de PIN encolados: cuentas que entraron al club sin PIN. */
  pin_mails: number
}

export interface AdminClubStaff {
  id: number
  club_id: number
  user_id: number
  username: string
  email: string | null
  club_role: 'manager'
  added_by_user_id: number | null
  created_at: string
  /** Alta/último login del User (auth_user), no del rol de staff. */
  date_joined: string | null
  last_login: string | null
}

export interface AdminEmailRow {
  id: number
  kind: string
  /** Destinatario REAL, siempre — aunque el envío haya salido re-ruteado por
   *  `EMAIL_OVERRIDE_RECIPIENT` (eso lo dice `override_applied`). */
  recipient: string
  subject: string
  status: AdminEmailStatus
  created_at: string | null
  scheduled_for: string | null
  sent_at: string | null
  attempts: number
  last_error: string | null
  /** El envío NO fue al destinatario real: salió re-ruteado al override. */
  override_applied: boolean
  /** Cuándo un superadmin lo mandó con "Enviar ahora" (salteando el freno). */
  forced_at: string | null
}

/** Estado del freno global del outbox: como máximo un mail cada
 *  `min_interval_s`, el resto espera en cola. Viaja junto a la lista para que
 *  la pantalla pueda decir a qué hora sale el próximo. */
export interface AdminEmailThrottle {
  min_interval_s: number
  sending_enabled: boolean
  /** Dirección a la que se re-rutea TODO el correo, o null (lo normal). */
  override_recipient: string | null
  email_configured: boolean
  last_sent_at: string | null
  /** Momento en que el freno vuelve a permitir un envío. */
  next_slot_at: string | null
  /** Cuándo sale el próximo mail concreto de la cola (null si no hay). */
  next_send_at: string | null
}

/** `failed` es TERMINAL (agotó los reintentos); un reintento con backoff
 *  sigue siendo `queued`. `skipped` está reservado para descartes manuales. */
export type AdminEmailStatus = 'queued' | 'sent' | 'failed' | 'skipped'

/** Código de acceso vive en el KIT — un club puede tener varios kits. */
export interface AdminKit {
  id: number
  club_id: number
  label: string | null
  access_code: string
  /** Raspberry del kit físico vinculada a este kit ("kit unificado", mg
   *  2026-09-07). `null` = kit de login sin placa. */
  rpi_id: string | null
  active: boolean
  created_at: string
}

export const admin = {
  stats: () => api.get<AdminStats>('/admin/stats'),

  impersonate: (userId: number) =>
    api.post<{ ok: boolean; user_id: number; username: string }>(
      `/admin/impersonate/${userId}`,
      {},
    ),

  // --- Clubes (PRD §8.4) ---
  clubs: () => api.get<{ clubs: AdminClub[] }>('/admin/clubs'),
  club: (id: number) => api.get<AdminClubDetail>(`/admin/clubs/${id}`),
  createClub: (name: string, seatLimit?: number | null) =>
    api.post<AdminClub>('/admin/clubs', { name, seat_limit: seatLimit ?? null }),
  updateClub: (
    id: number,
    patch: Partial<{
      name: string
      logo_url: string
      primary_color: string
      secondary_color: string
      accent_color: string
      seat_limit: number | null
      division: string
      /** ISO date (YYYY-MM-DD) o null explícito = sin vencimiento. */
      service_until: string | null
    }>,
  ) => api.patch<AdminClub>(`/admin/clubs/${id}`, patch),
  /** Suma jugadores al club. Se identifican por email (lo que el operador
   *  conoce) o por user_id cuando se eligieron de la búsqueda. Un email sin
   *  cuenta devuelve 404 nombrándolo, sin asignar a nadie del batch. */
  assignPlayers: (clubId: number, target: AdminPlayersTarget) =>
    api.post<AdminAssignPlayersResult>(`/admin/clubs/${clubId}/players`, {
      user_ids: target.user_ids ?? [],
      emails: target.emails ?? [],
    }),
  clubStaff: (clubId: number) =>
    api.get<{ staff: AdminClubStaff[] }>(`/admin/clubs/${clubId}/staff`),
  addClubStaff: (
    clubId: number,
    target: AdminStaffTarget,
    clubRole: AdminClubStaff['club_role'],
  ) =>
    api.post<AdminClubStaff>(`/admin/clubs/${clubId}/staff`, {
      ...target,
      club_role: clubRole,
    }),
  removeClubStaff: (clubId: number, staffId: number) =>
    api.delete<{ club_id: number; staff_id: number; removed: boolean }>(
      `/admin/clubs/${clubId}/staff/${staffId}`,
    ),
  createStaffInvite: (
    clubId: number,
    payload: { club_role: AdminStaffInvite['club_role']; email?: string; expires_in_days?: number },
  ) =>
    api.post<AdminCreatedStaffInvite>(
      `/admin/clubs/${clubId}/staff-invites`,
      payload,
    ),
  staffInvites: (clubId: number) =>
    api.get<{ invitations: AdminStaffInvite[] }>(`/admin/clubs/${clubId}/staff-invites`),

  // --- Kits (código de acceso vive en el kit; un club puede tener varios) ---
  clubKits: (clubId: number) =>
    api.get<{ kits: AdminKit[] }>(`/admin/clubs/${clubId}/kits`),
  /** `accessCode` manual opcional (ej. KIT1); sin él el backend lo genera. */
  createKit: (clubId: number, label?: string, rpiId?: string, accessCode?: string) =>
    api.post<AdminKit>(`/admin/clubs/${clubId}/kits`, {
      ...(label ? { label } : {}),
      ...(rpiId ? { rpi_id: rpiId } : {}),
      ...(accessCode ? { access_code: accessCode } : {}),
    }),
  /** Alta con el club en el body: la usa `/admin/devices` para crear el kit de
   *  una RPi recién conectada sin navegar al club. */
  createKitForRpi: (clubId: number, rpiId: string, label?: string) =>
    api.post<AdminKit>('/admin/kits', {
      club_id: clubId,
      rpi_id: rpiId,
      ...(label ? { label } : {}),
    }),
  regenerateKitCode: (kitId: number) =>
    api.post<AdminKit>(`/admin/kits/${kitId}/regenerate-code`, {}),
  updateKit: (
    kitId: number,
    // `rpi_id: null` explícito desvincula la placa; omitirlo la deja como está.
    patch: Partial<{
      active: boolean
      label: string
      club_id: number
      access_code: string
      rpi_id: string | null
    }>,
  ) => api.patch<AdminKit>(`/admin/kits/${kitId}`, patch),

  // --- Emails transaccionales ---
  emails: (params?: {
    status?: AdminEmailStatus
    kind?: string
    limit?: number
    offset?: number
  }) => {
    const q = new URLSearchParams()
    if (params?.status) q.set('status', params.status)
    if (params?.kind) q.set('kind', params.kind)
    q.set('limit', String(params?.limit ?? 50))
    q.set('offset', String(params?.offset ?? 0))
    return api.get<{
      items: AdminEmailRow[]
      total: number
      throttle: AdminEmailThrottle
    }>(`/admin/emails?${q.toString()}`)
  },

  /** "Enviar ahora": saltea el freno global una vez, para ese mail. Queda
   *  auditado (`forced_at` / `forced_by_user_id`). */
  emailSendNow: (id: number) =>
    api.post<{ email: AdminEmailRow }>(`/admin/emails/${id}/send-now`, {}),

  // --- Questionnaire editor ---
  qEditorList: () =>
    api.get<{ validated: QEditorValidatedSummary[]; custom: QEditorCustomSummary[] }>(
      '/admin/questionnaire-editor/list',
    ),
  /** Catálogo completo para el visor de solo lectura. */
  qCatalog: () =>
    api.get<{ items: QCatalogEntry[]; response_count_means: string }>(
      '/admin/questionnaire-editor/catalog',
    ),
  qEditorValidated: (code: string) =>
    api.get<{ read_only: true; definition: QEditorDefinition }>(
      `/admin/questionnaire-editor/validated/${encodeURIComponent(code)}`,
    ),
  qEditorCustom: (id: number) =>
    api.get<{
      id: number
      code: string
      name_es: string
      archived: boolean
      versions: QEditorVersion[]
    }>(`/admin/questionnaire-editor/custom/${id}`),
  qEditorCreate: (payload: {
    code: string
    name_es: string
    clone_from?: string | null
    definition?: QEditorDefinition | null
  }) =>
    api.post<{
      id: number
      code: string
      name_es: string
      draft_version_id: number
      version_no: number
    }>('/admin/questionnaire-editor/custom', payload),
  qEditorNewVersion: (id: number, baseVersionId?: number | null) =>
    api.post<QEditorVersion>(`/admin/questionnaire-editor/custom/${id}/versions`, {
      base_version_id: baseVersionId ?? null,
    }),
  qEditorSaveDraft: (versionId: number, definition: QEditorDefinition) =>
    api.patch<{ version: QEditorVersion; errors: string[] }>(
      `/admin/questionnaire-editor/version/${versionId}`,
      { definition },
    ),
  qEditorValidate: (versionId: number, definition?: QEditorDefinition) =>
    api.post<{ errors: string[] }>(
      `/admin/questionnaire-editor/version/${versionId}/validate`,
      { definition: definition ?? null },
    ),
  qEditorPublish: (versionId: number) =>
    api.post<{ version: QEditorVersion; already_published?: boolean }>(
      `/admin/questionnaire-editor/version/${versionId}/publish`,
    ),
  qEditorArchive: (id: number) =>
    api.post<{ archived: boolean }>(
      `/admin/questionnaire-editor/custom/${id}/archive`,
    ),
  qEditorDistribute: (id: number, emails: string[], timepoint?: string | null) =>
    api.post<{ created: number; reused: number; failed: { email: string; reason: string }[] }>(
      `/admin/questionnaire-editor/custom/${id}/distribute`,
      { emails, timepoint: timepoint ?? null },
    ),
  users: (search?: string, limit?: number) => {
    const qs = new URLSearchParams()
    if (search) qs.set('search', search)
    if (limit) qs.set('limit', String(limit))
    const s = qs.toString()
    return api.get<{ total: number; users: AdminUserRow[] }>(`/admin/users${s ? `?${s}` : ''}`)
  },
  user: (id: number) => api.get<AdminUserDetail>(`/admin/users/${id}`),
  athleteTimeline: (userId: number) =>
    api.get<AthleteTimeline>(`/admin/athletes/${userId}/timeline`),
  /** Direct download URL (anchor href) for the tidy CSV export. Respects the
   *  same `/api` prefix the SPA uses. Not a fetch — the browser downloads it. */
  exportDatasetUrl: (params?: { code?: string; club_id?: number; from?: string; to?: string; pseudonymize?: boolean }) => {
    const q = new URLSearchParams()
    if (params?.code) q.set('code', params.code)
    if (params?.club_id != null) q.set('club_id', String(params.club_id))
    if (params?.from) q.set('from', params.from)
    if (params?.to) q.set('to', params.to)
    if (params?.pseudonymize) q.set('pseudonymize', 'true')
    const qs = q.toString()
    return `${API_BASE}/admin/export/dataset.csv${qs ? `?${qs}` : ''}`
  },
  createUser: (payload: {
    email: string
    password?: string
    display_name?: string
    initial_role?: 'tester'
    /** Asigna el jugador a un club (sin `club_role`) o crea un `ClubStaff`
     *  (con `club_role`) — ver POST /admin/users en admin.py. Sin
     *  `club_role`, un alta de jugador nuevo encola el mail de setup de PIN. */
    club_id?: number
    club_role?: 'manager'
  }) => api.post<AdminUserRow>('/admin/users', payload),
  updateUser: (id: number, patch: Partial<{ password: string; display_name: string; email: string; is_active: boolean; club_id: number | null }>) =>
    api.patch<AdminUserRow & { changed: string[] }>(`/admin/users/${id}`, patch),

  // --- Capa científica (PRD §8.7) ---
  consents: (userId: number) =>
    api.get<AdminConsent[]>(`/admin/users/${userId}/consents`),
  recordConsent: (
    userId: number,
    payload: Partial<{ consent_version: string; is_parental: boolean; notes: string }>,
  ) => api.post<AdminConsent>(`/admin/users/${userId}/consents`, payload),
  revokeConsent: (userId: number, consentId: number) =>
    api.post<AdminConsent>(`/admin/users/${userId}/consents/${consentId}/revoke`, {}),
  enrollment: (userId: number) =>
    api.get<AdminEnrollment[]>(`/admin/users/${userId}/enrollment`),
  setEnrollment: (
    userId: number,
    payload: {
      meets_inclusion: boolean
      study_name?: string
      exclusion_reason?: string | null
      notes?: string
    },
  ) => api.post<AdminEnrollment>(`/admin/users/${userId}/enrollment`, payload),
  deleteEnrollment: (userId: number, enrollmentId: number) =>
    api.delete<void>(`/admin/users/${userId}/enrollment/${enrollmentId}`),
  /** Identity-map download URL — superadmin-only; custody offline per protocol §7.2. */
  identityMapUrl: () => `${API_BASE}/admin/export/identity-map.csv`,
  toggleAccess: (id: number, role: 'tester', action: 'grant' | 'revoke') =>
    api.post<{ user_id: number; roles: string[]; has_demo_access: boolean }>(
      `/admin/users/${id}/access`,
      { role, action },
    ),
  toggleQuestionnaireAccess: (id: number, code: string, action: 'revoke' | 'restore') =>
    api.post<{ user_id: number; code: string; revoked: boolean }>(
      `/admin/users/${id}/questionnaire-access`,
      { code, action },
    ),
  sessions: (params?: {
    limit?: number
    status?: string
    user_id?: number
    since_days?: number
    /** Sólo las que el servidor verificó que NO están listas para análisis. */
    incomplete?: boolean
  }) => {
    const q = new URLSearchParams()
    if (params?.limit) q.set('limit', String(params.limit))
    if (params?.status) q.set('status', params.status)
    if (params?.user_id) q.set('user_id', String(params.user_id))
    if (params?.since_days) q.set('since_days', String(params.since_days))
    if (params?.incomplete) q.set('incomplete', 'true')
    const qs = q.toString()
    return api.get<{ total: number; sessions: AdminSessionRow[] }>(
      `/admin/sessions${qs ? `?${qs}` : ''}`,
    )
  },
  session: (id: string) =>
    api.get<{ session: Record<string, unknown>; trials: Array<Record<string, unknown>> }>(
      `/admin/sessions/${encodeURIComponent(id)}`,
    ),
  /** Silver de la sesión: manifests + ciclos parseados (superadmin). */
  getSessionSilver: (id: string) =>
    api.get<SessionSilver>(`/admin/sessions/${encodeURIComponent(id)}/silver`),
  /** Anchor href: zip con bronce + silver + session.json. No es fetch. */
  sessionArtifactsZipUrl: (id: string) =>
    `${API_BASE}/admin/sessions/${encodeURIComponent(id)}/artifacts.zip`,
  /** Anchor href: dataset científico integrado (una fila por trial). */
  trialsCsvUrl: (params?: {
    club_id?: number
    condition?: string
    session_id?: string
    from?: string
    to?: string
    pseudonymize?: boolean
  }) => {
    const q = new URLSearchParams()
    if (params?.club_id != null) q.set('club_id', String(params.club_id))
    if (params?.condition) q.set('condition', params.condition)
    if (params?.session_id) q.set('session_id', params.session_id)
    if (params?.from) q.set('from', params.from)
    if (params?.to) q.set('to', params.to)
    if (params?.pseudonymize) q.set('pseudonymize', 'true')
    const qs = q.toString()
    return `${API_BASE}/admin/export/trials.csv${qs ? `?${qs}` : ''}`
  },
  /** Anchor href: reporte PDF por atleta (`role_view` decide el contenido). */
  athleteReportPdfUrl: (userId: number, roleView: ReportRoleView = 'coach') =>
    `${API_BASE}/admin/athletes/${userId}/report.pdf?role_view=${roleView}`,
  authEvents: (params?: { kind?: string; since_days?: number }) => {
    const q = new URLSearchParams()
    if (params?.kind) q.set('kind', params.kind)
    if (params?.since_days) q.set('since_days', String(params.since_days))
    const qs = q.toString()
    return api.get<{ total: number; events: AdminAuthEvent[] }>(
      `/admin/auth-events${qs ? `?${qs}` : ''}`,
    )
  },
  questionnaires: () =>
    api.get<{ items: AdminQuestionnaireSummary[] }>('/admin/questionnaires/catalog'),
  questionnaireResponses: (code?: string) =>
    api.get<{ total: number; items: AdminQResponseRow[] }>(
      `/admin/questionnaires/responses${code ? `?code=${encodeURIComponent(code)}` : ''}`,
    ),
  invitations: (params?: { code?: string; status?: string }) => {
    const q = new URLSearchParams()
    if (params?.code) q.set('code', params.code)
    if (params?.status) q.set('status', params.status)
    const qs = q.toString()
    return api.get<{ items: AdminInvitation[] }>(
      `/admin/invitations${qs ? `?${qs}` : ''}`,
    )
  },
  createInvitation: (email: string, code: string, timepoint?: string) =>
    api.post<{
      invitation: AdminInvitation
      link_url: string
      email_sent: boolean
    }>('/admin/invitations', {
      email,
      code,
      ...(timepoint ? { timepoint } : {}),
    }),
  bulkInvitations: (emails: string[], code: string, timepoint?: string) =>
    api.post<{
      created: number
      reused: number
      failed: Array<{ email: string; reason: string }>
    }>('/admin/invitations/bulk', {
      emails,
      code,
      ...(timepoint ? { timepoint } : {}),
    }),
  resendInvitation: (id: number) =>
    api.post<{ email_sent: boolean; invitation: AdminInvitation }>(
      `/admin/invitations/${id}/resend`,
    ),
  revokeInvitation: (id: number) =>
    api.delete<{ invitation: AdminInvitation }>(`/admin/invitations/${id}`),
  questionnaireResponse: (id: number) =>
    api.get<{
      id: number
      code: string
      timepoint: string
      user: { id: number; username: string } | null
      submitted_name: string | null
      submitted_email: string | null
      submitted_at: string
      ip: string | null
      user_agent: string | null
      scores: AdminQResponseRow['scores']
      answers: Record<string, number>
      definition: { code: string; name_es: string } | null
      items_view: Array<{
        id: number
        text?: string
        subscale?: string
        reverse?: boolean
        value: number | null
        label?: string | null
      }>
    }>(`/admin/questionnaires/responses/${id}`),
}
